import { useEffect, useMemo, useState } from "react";

import {
  patchSangjoQuotationMemo,
  submitSangjoQuotationRequest,
} from "@/apis/sangjoQuotation";
import { ExpectedPriceRangeBar } from "@/components/ExpectedPriceRangeBar";
import { customEvent, PixelEventName } from "goi_common";

const PREPARE_STATUS_OPTIONS = [
  "급하지 않지만 미리 알아보려고 해요.",
  "1주에서 한 달 정도 기간이 남은 것 같아요.",
  "임종이 며칠 남지 않았어요.",
  "임종하신 상태입니다.",
];

const MOURNER_OPTIONS = [
  "0명(무빈소)",
  "50명 내외",
  "100명 내외",
  "200명 이상",
  "모르겠어요",
];

const NATIONAL_MIN_PRICE = 38;
const NATIONAL_MAX_PRICE = 1205;

interface QuoteQuickEstimateSectionProps {
  parlorId: number;
  parlorName: string;
  sidoCode: string;
  sigunguCode: string;
  costMubinso: number;
  costFamily: number;
  costGeneral: number;
}

const getGaugeRange = ({
  mournerCount,
  costMubinso,
  costFamily,
  costGeneral,
}: Pick<
  QuoteQuickEstimateSectionProps,
  "costMubinso" | "costFamily" | "costGeneral"
> & {
  mournerCount: string;
}) => {
  if (mournerCount === "0명(무빈소)") {
    return {
      min: costMubinso,
      max: Math.min(costGeneral, Math.round(costMubinso * 1.2)),
    };
  }
  if (mournerCount === "50명 내외") {
    return {
      min: Math.round(costFamily * 0.85),
      max: Math.round(costFamily * 1.05),
    };
  }
  if (mournerCount === "100명 내외") {
    return {
      min: costFamily,
      max: Math.round((costFamily + costGeneral) / 2),
    };
  }
  if (mournerCount === "200명 이상") {
    return {
      min: Math.round((costFamily + costGeneral) / 2),
      max: costGeneral,
    };
  }
  return { min: costMubinso, max: costGeneral };
};

export const QuoteQuickEstimateSection = ({
  parlorId,
  parlorName,
  sidoCode,
  sigunguCode,
  costMubinso,
  costFamily,
  costGeneral,
}: QuoteQuickEstimateSectionProps) => {
  const [status, setStatus] = useState(PREPARE_STATUS_OPTIONS[0]);
  const [mournerCount, setMournerCount] = useState(MOURNER_OPTIONS[2]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const range = useMemo(
    () => getGaugeRange({ mournerCount, costMubinso, costFamily, costGeneral }),
    [mournerCount, costMubinso, costFamily, costGeneral]
  );

  useEffect(() => {
    const resetSubmitting = () => setIsSubmitting(false);

    window.addEventListener("pageshow", resetSubmitting);
    return () => window.removeEventListener("pageshow", resetSubmitting);
  }, []);

  const submitCostRequest = async () => {
    if (isSubmitting) return;

    customEvent({
      newGtm: {
        path: "parlor_ad_quote",
        trigger_and_target: "btn_cta_quote",
        properties: {
          parlor_id: parlorId,
          parlor_name: parlorName,
          flow: "direct_quote",
          mourner_count: mournerCount,
        },
      },
      pixel: PixelEventName.AddPaymentInfo,
    });

    setIsSubmitting(true);
    try {
      const response = await submitSangjoQuotationRequest({
        status,
        funeral_sido: sidoCode,
        funeral_sigungu: sigunguCode,
        needs_binso: mournerCount !== "0명(무빈소)",
        condolers:
          mournerCount === "모르겠어요"
            ? ""
            : mournerCount === "0명(무빈소)"
            ? "0명"
            : mournerCount,
      });

      await patchSangjoQuotationMemo(
        response.data.id,
        `${parlorName} 페이지에서 제출한 견적`
      ).catch(() => {});

      customEvent({
        newGtm: {
          action: "match_request_complete",
          properties: {
            entry: "parlor_ad_quote_direct",
            parlor_id: parlorId,
            mourner_count: mournerCount,
          },
        },
      });
      customEvent({
        newGtm: {
          path: "parlor_ad_quote",
          trigger_and_target: "quick_success",
          properties: {
            parlor_id: parlorId,
            parlor_name: parlorName,
            mourner_count: mournerCount,
            status,
          },
        },
      });

      setIsSubmitting(false);

      /**
       * 재제출(v2 200 = 기존 견적 merge)이면 로그인을 거치지 않고 결과 페이지로 보낸다.
       * 이미 로그인한 사람이라 로그인 화면이 곧바로 `redirectTo`로 되돌리는데, 그 목적지가
       * 견적 폼이라 방금 제출한 사람에게 빈 폼이 다시 보인다.
       *
       * parlor에는 전역 로그인 상태가 없어 상태코드가 유일한 판정 근거다. 201(신규 생성)은
       * 비회원이거나 견적이 없던 회원이라 기존대로 로그인 handoff를 태운다.
       */
      if (response.status === 200) {
        window.location.assign(
          `${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/recommendation/`
        );
        return;
      }

      /*
       * 현재 페이지 쿼리를 물려받으므로 광고 파라미터(`gclid`, `n_query`, `n_ad`)가 그대로
       * platform 까지 간다. 판정은 platform 의 `hasAdEntryParams` 한 곳에서만 한다.
       */
      const loginParams = new URLSearchParams(window.location.search);
      loginParams.set("redirectTo", "/match/request/");
      loginParams.set("event_from", "parlor_ad_quote_direct");
      loginParams.set("qid", String(response.data.id));
      loginParams.set("status", response.data.status);
      loginParams.set("sido", response.data.funeral_sido);
      loginParams.set("sigungu", response.data.funeral_sigungu);
      // 어느 장례식장 견적인지. platform 은 크로스도메인이라 쿠키로 받을 수 없다
      loginParams.set("parlor_id", String(parlorId));

      const loginQuery = loginParams.toString().replace(/\+/g, "%20");
      window.location.assign(
        `${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/login?${loginQuery}`
      );
    } catch (error) {
      customEvent({
        newGtm: {
          path: "parlor_ad_quote",
          trigger_and_target: "quick_error",
          properties: {
            parlor_id: parlorId,
            parlor_name: parlorName,
          },
        },
      });
      console.error("Quote landing cost request failed", error);
      alert(
        "잠시 오류가 발생했습니다. 문제가 지속되면 1666-9784로 연락해주세요."
      );
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="lp-cost-wrap">
        <ExpectedPriceRangeBar
          min={range.min}
          max={range.max}
          MIN_PRICE={NATIONAL_MIN_PRICE}
          MAX_PRICE={NATIONAL_MAX_PRICE}
        />
      </div>
      <section className="lp-pitch">
        <p className="lp-pitch-copy">
          장례식장 빈소, 음식, 용품 이용료를 포함한 비용을
          <br />
          <span className="hl">1분만에</span> 확인해보세요.
        </p>
        <div className="lp-quick-estimate__form">
          <label>
            예상 조문객 수
            <select
              value={mournerCount}
              onChange={(event) => {
                setMournerCount(event.target.value);
                customEvent({
                  newGtm: {
                    path: "parlor_ad_quote",
                    trigger_and_target: "quick_mourner",
                    properties: {
                      parlor_id: parlorId,
                      mourner_count: event.target.value,
                    },
                  },
                });
              }}
            >
              {MOURNER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label>
            장례 준비 시점
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                customEvent({
                  newGtm: {
                    path: "parlor_ad_quote",
                    trigger_and_target: "quick_status",
                    properties: {
                      parlor_id: parlorId,
                      status: event.target.value,
                    },
                  },
                });
              }}
            >
              {PREPARE_STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>

        <button
          type="button"
          className="lp-cta lp-quick-estimate__submit"
          onClick={submitCostRequest}
          disabled={isSubmitting}
        >
          {isSubmitting ? "견적 준비 중..." : "상세 비용 알아보기"}
          {!isSubmitting && <span className="arrow">→</span>}
        </button>
        <div className="lp-cta-sub">
          <span>비용 견적 무료 제공</span>
        </div>
        <p className="lp-quick-estimate__source">
          e하늘 장사정보시스템을 기반으로 고이 자체 추산 비용
        </p>
      </section>
    </>
  );
};
